import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { FieldChange } from 'src/lib/fliko/interfaces/fieldChange';
import { LandlordType, ResidentHistoryRecord } from 'src/lib/fliko/models/person/resident.data';
import { SubscribeComponent } from 'src/app/shared/classes/subscribe-component';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ResidentService } from '../../../services/resident.service';
import { UserService } from '../../../services/user.service';

interface NestedChanges {
  type: string;
  valueBefore: string | null;
  valueAfter: string | null;
  nested: FieldChange<string>[];
}

@Component({
  selector: 'app-users-resident-history',
  templateUrl: './users-resident-history.component.html',
  styleUrls: ['./users-resident-history.component.scss']
})
export class UsersResidentHistoryComponent extends SubscribeComponent {
  public records: ResidentHistoryRecord[] = [];
  public loaded = false;

  private residentId: string = '';
  private usersMap = new Map<string, string>();

  private readonly locHelper = new LocalizationHelper(this.ls);

  constructor(
    @Inject(MAT_DIALOG_DATA) residentId: string,
    private userService: UserService,
    private residentService: ResidentService,
    private ls: LocalizationService
  ) {
    super();
    this.residentId = residentId;
  }

  ngOnInit(): void {
    this.subscription$.add(
      this.residentService.getResidentHistoryRecords$(this.residentId).subscribe((history) => {
        this.records = (history ?? []).sort((a, b) => b.date - a.date);
        this.loaded = true;
      })
    );

    this.subscription$.add(
      this.userService.getUsers$().subscribe((users) => (this.usersMap = new Map(users.map((u) => [u.id, ToString.fullName(u)]))))
    );
  }

  public transformChanges(changes: FieldChange<string>[]): NestedChanges[] {
    const ret: NestedChanges[] = [];
    const nestedMap = new Map<string, FieldChange<string>[]>();
    changes?.forEach((change) => {
      if (change.type.includes('.')) {
        const keys = change.type.split('.');
        let nestObj = nestedMap.get(keys[0]);
        if (!nestObj) {
          nestObj = [];
          nestedMap.set(keys[0], nestObj);
        }
        nestObj.push({ ...change, type: keys[1] });
      } else {
        ret.push({ ...change, nested: [] });
      }
    });
    nestedMap.forEach((value, key) => {
      ret.push({ type: key, valueAfter: null, valueBefore: null, nested: value ?? [] });
    });
    return ret;
  }

  public changeTitle(change: FieldChange<string>): string {
    switch (change.type) {
      case 'name':
      case 'ownerCode':
      case 'birthDate':
      case 'documentNo':
      case 'userId':
        return `user.${change.type}`;
      case 'phone':
      case 'email':
      case 'PESEL':
      case 'address':
        return `label.${change.type.toLocaleLowerCase()}`;
      case 'country':
      case 'city':
      case 'zipCode':
      case 'lastName':
        return 'user.surname';
      case 'street':
        return `addr.${change.type}`;
      case 'number':
        return 'addr.blockUnit';
      case 'type':
        return 'user.residentType';
      case 'emergencyContact':
        return 'user.emergeContact';
      case 'nationality':
        return 'user.national';
      default:
        return change.type;
    }
  }

  public changeValue(change: FieldChange<string>): string {
    let before: string = change.valueBefore ?? '';
    let after: string = change.valueAfter ?? '';
    const trans = (x: string) => this.ls.translate(x);
    const transCap = (x: string) => StrHlp.capitalize(trans(x));
    switch (change.type) {
      case 'birthDate': {
        const fd = (x: number) => this.locHelper.localizeDate(x, 'dShort');
        before = fd(+before);
        after = fd(+after);
        break;
      }
      case 'type': {
        const tp = (type: LandlordType) => trans(<LandlordType>type === LandlordType.COMPANY ? 'label.firm' : 'user.person');
        before = tp(<LandlordType>before);
        after = tp(<LandlordType>after);
        break;
      }
      case 'userId':
        before = before ? (this.usersMap.get(before) ?? 'N/A') : '';
        after = after ? (this.usersMap.get(after) ?? 'N/A') : '';
        break;
      case 'phone':
      case 'email': {
        const sBefore = before ? before.split('|') : [];
        const sAfter = after ? after.split('|') : [];
        const added = sAfter.filter((a) => !sBefore.includes(a));
        const removed = sBefore.filter((b) => !sAfter.includes(b));
        const text: string[] = [];
        if (added.length > 0) text.push(`${transCap('rHistory.added')} ${this.bold(added.join(', '))}`);
        if (removed.length > 0) text.push(`${transCap('rHistory.removed')} ${this.bold(removed.join(', '))}`);
        return text.join(', ');
      }
    }
    return (
      `${ObjHlp.haveValue(before) ? this.bold(before) : trans('label.empty')} -> ` +
      `${ObjHlp.haveValue(after) ? this.bold(after) : trans('label.empty')}`
    );
  }

  private bold(value: string): string {
    return `<b>${value}</b>`;
  }
}
